// ═══════════════════════════════════════════════
//   POSTGAME · End-of-campaign closure window.
//
//   Auto-opens once on first login past Day 11 if the user had any
//   campaign activity (claimed daily stamps or replied to story emails).
//   Resolves the ending from S.mailEnding, shows campaign stats, the
//   real Dokomi booth info, and a closing note from Clow.
//
//   Replay entry: ✦ HOW IT ENDED button in the taskbar (replaces the
//   tutorial's ♡ HOW TO PLAY after May 28). To restore HOW TO PLAY
//   after Dokomi, remove the initTaskbarPostgameToggle() call from
//   auth.js → enterDesktop.
// ═══════════════════════════════════════════════

const { S, scheduleSave, openWin, closeWin } = window;

const POSTGAME_LS_KEY = 'postgame-seen-v1';

const POSTGAME_ENDING_SUMMARIES = {
    good: {
        icon: '🏪',
        name: 'EVERYTHING IN ORDER',
        summary: "Eleven days, every stamp, every reply. The shop still felt like itself when she came back.",
    },
    remembers: {
        icon: '🌀',
        name: 'THE KONBINI THAT REMEMBERS',
        summary: "You showed up. The shop made habits in your shape. Turns out some habits are good ones ✦",
    },
    space: {
        icon: '🚪',
        name: 'THE SPACE BETWEEN SHELVES',
        summary: "Something slipped through. She left things behind too. You'll sort it out together.",
    },
    documented: {
        icon: '📋',
        name: 'DOCUMENTED',
        summary: "You wrote it down. You said the names back. Ten years of cover and you were the first to read it.",
    },
    closed: {
        icon: '🔦',
        name: 'TEMPORARILY CLOSED',
        summary: "The lights stayed on. That counted. She's coming home, and you'll fix the rest together.",
    },
};

const POSTGAME_NULL_ENDING = {
    icon: '✦',
    name: 'NO ENDING RECORDED',
    summary: "You stopped by. She's still glad you did. The booth's open anyway.",
};

function _lsPostGameSeen() {
    try { return localStorage.getItem(POSTGAME_LS_KEY) === '1'; }
    catch (e) { return false; }
}

function _markPostGameSeen() {
    S.postGameSeen = true;
    try { localStorage.setItem(POSTGAME_LS_KEY, '1'); } catch (e) {}
    if (typeof scheduleSave === 'function') scheduleSave();
}

function _userHadCampaignActivity() {
    const claimed = (S.dailyState && S.dailyState.claimed) || [];
    const replied = S.mailStoryChoices || {};
    return claimed.length > 0 || Object.keys(replied).length > 0;
}

function _bestGameScore() {
    const scores = S.gameTopScores || {};
    const values = Object.values(scores).filter(v => typeof v === 'number' && v > 0);
    if (values.length === 0) return 0;
    return Math.max(...values);
}

function openPostGame() {
    openWin('w-postgame');
    const win = document.getElementById('w-postgame');
    if (win) {
        const z = parseFloat(getComputedStyle(document.body).zoom) || 1;
        const vw = window.innerWidth / z;
        const w = win.offsetWidth || 520;
        win.style.top  = '40px';
        win.style.left = Math.max(20, Math.round((vw - w) / 2)) + 'px';
    }
    renderPostGame();
}

function renderPostGame() {
    const scroll = document.getElementById('pg-scroll');
    if (!scroll) return;

    const endingKey = S.mailEnding;
    const ending = endingKey && POSTGAME_ENDING_SUMMARIES[endingKey]
        ? POSTGAME_ENDING_SUMMARIES[endingKey]
        : POSTGAME_NULL_ENDING;

    const days = (S.dailyState && S.dailyState.claimed) ? S.dailyState.claimed.length : 0;
    const replies = S.mailStoryChoices ? Object.keys(S.mailStoryChoices).length : 0;
    const pts = S.pts || 0;
    const bestScore = _bestGameScore();
    const aliens = (S.myAliens || []).length;

    scroll.innerHTML = `
        <div class="pg-hero">
            <div class="pg-pill">CAMPAIGN COMPLETE</div>
            <div class="pg-title">CLOSING TIME</div>
            <div class="pg-sub">The arc is closed. The booth is open.</div>
        </div>

        <div class="pg-section">
            <div class="pg-section-label">YOUR ENDING</div>
            <div class="pg-ending">
                <div class="pg-ending-icon">${ending.icon}</div>
                <div class="pg-ending-meta">
                    <div class="pg-ending-name">${ending.name}</div>
                    <div class="pg-ending-summary">${ending.summary}</div>
                </div>
            </div>
        </div>

        <div class="pg-section">
            <div class="pg-section-label">YOUR RUN</div>
            <div class="pg-stats">
                <div class="pg-stat">
                    <div class="pg-stat-n">${days}</div>
                    <div class="pg-stat-l">/ 11 DAYS</div>
                </div>
                <div class="pg-stat">
                    <div class="pg-stat-n">${replies}</div>
                    <div class="pg-stat-l">REPLIES</div>
                </div>
                <div class="pg-stat">
                    <div class="pg-stat-n">${pts}</div>
                    <div class="pg-stat-l">POINTS</div>
                </div>
                <div class="pg-stat">
                    <div class="pg-stat-n">${bestScore}</div>
                    <div class="pg-stat-l">BEST SCORE</div>
                </div>
                <div class="pg-stat">
                    <div class="pg-stat-n">${aliens}</div>
                    <div class="pg-stat-l">ALIENS</div>
                </div>
            </div>
        </div>

        <div class="pg-section">
            <div class="pg-section-label">THE BOOTH IS OPEN</div>
            <div class="pg-booth">
                <div class="pg-booth-where">HALL 3 · BOOTH 3X09 · DÜSSELDORF</div>
                <div class="pg-booth-days">
                    <div class="pg-booth-day"><span class="pg-booth-d">FRI · 29 MAY</span><span class="pg-booth-h">10:00–19:00</span></div>
                    <div class="pg-booth-day"><span class="pg-booth-d">SAT · 30 MAY <span class="pg-booth-bz">★ BUSY</span></span><span class="pg-booth-h">09:00–19:00</span></div>
                    <div class="pg-booth-day"><span class="pg-booth-d">SUN · 31 MAY</span><span class="pg-booth-h">09:00–17:00</span></div>
                </div>
                <div class="pg-booth-extras">
                    <span class="pg-booth-tag">FREE STICKER · FIRST 20</span>
                    <span class="pg-booth-tag">♥ COSPLAY OK</span>
                </div>
            </div>
        </div>

        <div class="pg-section">
            <div class="pg-section-label">WHAT'S NEXT</div>
            <div class="pg-next">The konbini doesn't close. New content coming.</div>
        </div>

        <div class="pg-clow">
            <div class="pg-clow-portrait">✦</div>
            <div class="pg-clow-body">
                eleven days ✦ you held it down.<br><br>
                booth 3X09 from friday. i'll be there ✦
            </div>
        </div>
    `;
}

function closePostGame() {
    _markPostGameSeen();
    closeWin('w-postgame');
}

function maybeShowPostGameOnEnter() {
    if (typeof window.kmGameDay !== 'function') return false;
    if (window.kmGameDay() <= 11) return false;
    if (!_userHadCampaignActivity()) return false;
    if (S.postGameSeen || _lsPostGameSeen()) {
        S.postGameSeen = true;
        return false;
    }
    setTimeout(() => openPostGame(), 1500);
    return true;
}

function initTaskbarPostgameToggle() {
    const tutBtn = document.getElementById('tb-tut-btn');
    const endBtn = document.getElementById('tb-end-btn');
    if (!tutBtn || !endBtn) return;
    const day = (typeof window.kmGameDay === 'function') ? window.kmGameDay() : 0;
    if (day > 11) {
        tutBtn.style.display = 'none';
        endBtn.style.display = '';
    } else {
        tutBtn.style.display = '';
        endBtn.style.display = 'none';
    }
}

window.openPostGame              = openPostGame;
window.closePostGame             = closePostGame;
window.maybeShowPostGameOnEnter  = maybeShowPostGameOnEnter;
window.initTaskbarPostgameToggle = initTaskbarPostgameToggle;
